/**
 * Image-role actions — route a picked image into the generation's `source` or
 * `reference` role. The single home for the ADR-005 #10 per-image
 * "use as source / use as reference" behavior so every pick surface (gallery
 * picker, gallery waterfall, uploads / my-works) shares one implementation.
 *
 * Roles are realized DIFFERENTLY (ADR-005 #10, 2.3.0):
 *   - source    = the ONLY pixel input. A typed ref { origin, id } is recorded so
 *                 generate can re-validate + size-resolve it server-side; the URL
 *                 is also kept for the source preview / vision / aspect detection.
 *                 At most one source — picking a new one replaces it.
 *   - reference = a PROMPT carrier, not pixels. The picked image is run through
 *                 vision/analyze which returns inspiration CARDS; those cards are
 *                 surfaced (selected) in the middle column where the existing cards
 *                 UI already lets the user view / edit (= a NEW card) / deselect.
 *                 References accumulate — each pick adds a card bundle.
 *
 * Origin is EXPLICIT, never inferred from id shape (the two stores use different
 * id spaces — images=INTEGER, assets=ULID). Each call site passes the origin of
 * its surface: gallery library = 'library', uploads / my-works = 'asset'.
 */

import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { showToast } from '../../utils/toast.js'
import { analyzeImageVision, fetchImageCards } from '../../api/prompts.js'
import { uploadImage } from '../../api/index.js'
import { addReferenceCards } from './inspiration-workspace.js'
import { createLogger } from '../../utils/logger.js'
const log = createLogger('create-ui.image-roles')


/**
 * @typedef {Object} PickImage
 * @property {string}        url    display / pixel URL (CDN-resolved)
 * @property {string|number} id     store-native id (library=INTEGER, asset=ULID)
 * @property {string} [title]
 */

/**
 * Route an image into the SOURCE slot (the pixel base being edited / built on).
 * Replaces any current source. Updates `sourceImage` (the source-display
 * state the dialog already consumes) with an explicit typed-ref so generate can
 * send `source: { origin, id }`.
 *
 * @param {PickImage} image
 * @param {'library'|'asset'} origin
 */
export function useImageAsSource(image, origin, opts = {}) {
  const url = image?.url
  const id = image?.id
  if (!url || id == null) {
    showToast('Image unavailable as source', 'error')
    return
  }

  const existing = store.getState('sourceImage') || {}
  store.setState('sourceImage', {
    ...existing,
    url,
    // Typed slot ref (ADR-005 #10) — origin is explicit, id stringified.
    slotOrigin: origin,
    slotId: String(id),
    // Human-facing provenance label (kept for back-compat with existing reads).
    source: origin === 'library' ? 'gallery' : 'asset',
    id,
  })

  // Tell any open dialog to render this as the source preview (+ run vision for
  // its own auto-cards). The dialog's IMAGE_SELECT handler calls showSourceImage.
  // noVision: pass-through for picks whose pixels were already read (e.g. an
  // external source materialized into an asset at generate time).
  eventBus.emit(EVENTS.IMAGE_SELECT, { url, id, source: 'source-slot', noVision: !!opts.noVision })
  // silent: the Refine loop shows its own "now refining" toast — suppress the
  // duplicate "Set as source". why → Zeus click-path F4 2026-06-21.
  if (!opts.silent) showToast('Set as source', 'success', 1500)
}

/**
 * Materialize an external source URL (?injectImage / H-167 ?source=<url> deep
 * link) into a typed asset ref: fetch the pixels client-side and push them
 * through the EXISTING two-step upload (presign → PUT → finalize), then
 * re-slot as { origin:'asset' }. Kept lazy (generate time, not entry time) so
 * a bounce off the funnel costs nothing, and the generate contract never sees
 * a raw URL (SSRF, ADR-005 #10). noVision: the pixels were already read on
 * entry — don't pay twice. Moved here from create/index.js (H-167) so the
 * studio gallery shares the same compliance path.
 * @param {string} url
 */
export async function materializeExternalSource(url) {
  const resp = await fetch(url)
  if (!resp.ok) throw new Error(`image fetch failed (${resp.status})`)
  const blob = await resp.blob()
  const type = blob.type || 'image/png'
  const ext = (type.split('/')[1] || 'png').toLowerCase()
  const file = new File([blob], `external-source.${ext}`, { type })
  const fd = new FormData()
  fd.append('image', file)
  fd.append('source', 'source')
  const asset = await uploadImage(fd)
  const assetUrl = asset?.public_url || asset?.url
  if (!assetUrl || asset?.id == null) throw new Error('upload returned no asset')
  useImageAsSource({ url: assetUrl, id: asset.id }, 'asset', { silent: true, noVision: true })
}

/**
 * Read the current source slot as a typed ref for the generate body.
 * @returns {{ origin: 'library'|'asset', id: string } | null}
 */
export function getSourceSlotRef() {
  const ref = store.getState('sourceImage')
  if (!ref?.slotOrigin || ref?.slotId == null) return null
  return { origin: ref.slotOrigin, id: String(ref.slotId) }
}

/** True when the given image URL is the current source (for pick-surface highlight). */
export function isCurrentSource(url) {
  const ref = store.getState('sourceImage')
  return Boolean(ref?.url && url && ref.url === url)
}

/**
 * Read the current REFERENCE slot as a typed ref for the generate body (the
 * second pixel, two-image intents). ADR-005 2.4.1 / #10.
 * @returns {{ origin: 'library'|'asset', id: string } | null}
 */
export function getReferenceSlotRef() {
  const ref = store.getState('referenceSlot')
  if (!ref?.slotOrigin || ref?.slotId == null) return null
  return { origin: ref.slotOrigin, id: String(ref.slotId) }
}

/**
 * Clear ALL reference-slot state — the typed slot ref, the deprecated asset id,
 * and the 2nd-image url. Called on intent switch and on result-consume (Refine /
 * Keep / Discard) so a previous intent's reference can't be silently reused as
 * the next generation's 2nd pixel (a paid fal call with the wrong input).
 * why → Zeus click-path F2/N2 2026-06-21.
 */
export function clearReferenceState() {
  store.setState('referenceSlot', null)
  store.setState('creation.referenceAssetId', null)
  store.setState('creation.secondImageUrl', null)
}

/**
 * Route an image into the REFERENCE role: vision/analyze it into inspiration
 * cards, then surface those cards (selected) in the middle column. References
 * accumulate — each call adds a fresh bundle. The original image is never a
 * pixel input; only the prompt behind it (the cards) is used.
 *
 * @param {PickImage} image
 * @param {'library'|'asset'} origin  accepted for symmetry; vision takes the URL
 * @returns {Promise<void>}
 */
export async function useImageAsReference(image, origin) {
  const url = image?.url
  if (!url) {
    showToast('Image unavailable as reference', 'error')
    return
  }

  // ADR-005 2.4.1: reference is BOTH a second PIXEL frame (consumed by the
  // two-image fal model for Style Transfer / Try-On / Model Swap) AND a card
  // source. Record the typed pixel ref so generate can send `reference`; then
  // also vision-extract its cards below.
  if (image?.id != null) {
    store.setState('referenceSlot', {
      url,
      slotOrigin: origin,
      slotId: String(image.id),
      id: image.id,
    })
  }

  // Show the reference in its own preview slot (parallel to source). The dialog's
  // IMAGE_SELECT handler routes role:'reference' to showReferenceImage.
  eventBus.emit(EVENTS.IMAGE_SELECT, { url, id: image?.id, role: 'reference' })

  showToast('Reading style…', 'info', 2000)
  try {
    // BUNDLE-FIRST, VISION-FALLBACK (ADR-005 2.8.0 #2): the image's persisted
    // image_cards bundle is instant + curated + costs no fal/vision call; live
    // vision runs only when the image has no bundle.
    let cards = []
    const bundle = await fetchImageCards(image?.id ?? url)
    if (bundle?.has_cards && bundle.cards?.length) {
      cards = bundle.cards
    } else {
      const result = await analyzeImageVision(url)
      cards = result?.cards || result?.data?.cards || []
    }
    if (!cards.length) {
      showToast('No style cards found for this image', 'error')
      return
    }
    // Realize the reference as cards in the middle column (the existing cards
    // UI handles view / edit = NEW card / deselect). title←name mapped there.
    const added = addReferenceCards(cards)
    showToast(
      added > 0 ? `Added ${added} inspiration cards` : 'Style already added',
      'success',
      1800
    )
  } catch (err) {
    log.error('[image-roles] reference vision failed:', err)
    showToast('Could not analyze this image', 'error')
  }
}
